import { useEffect, useState, useRef } from "react";
import { MuiMaterials } from '../@index.js';
import SiteCard from './SiteCard.js';
import * as global from '../data/global';
import * as _ from 'lodash';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../provider/authProvider.js";
import { createApiEndpoint } from '../api/createApiEndpoint.js';
import axios from "axios";
import ConfirmDialog, { confirmDialogButtonSx } from './windowChrome/ConfirmDialog.js';

// Helper function to get theme-aware colors
const getThemeColor = (lightColor, darkColor) => {
    if (global.gw.theme === 'light') {
        return lightColor;
    }
    return darkColor;
};

const ALPHA_MODAL_SEEN_KEY = "grainwave_alphaModalSeen";

const alphaWelcomeDismissed = () => {
    try {
        const seen = sessionStorage.getItem(ALPHA_MODAL_SEEN_KEY) === "true"
            || localStorage.getItem(ALPHA_MODAL_SEEN_KEY) === "true";
        if (seen) {
            sessionStorage.setItem(ALPHA_MODAL_SEEN_KEY, "true");
            localStorage.setItem(ALPHA_MODAL_SEEN_KEY, "true");
        }
        return seen;
    } catch {
        return false;
    }
};

const rememberAlphaWelcomeDismissed = () => {
    try {
        sessionStorage.setItem(ALPHA_MODAL_SEEN_KEY, "true");
        localStorage.setItem(ALPHA_MODAL_SEEN_KEY, "true");
    } catch {
        /* private mode */
    }
};

export const CustomerSites = () => {
    const navigate = useNavigate();
    const { clearToken, setToken } = useAuth();
    const [isSitesLoaded, setIsSitesLoaded] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [forceUpdate, setForceUpdate] = useState(0);
    // Track expanded sites by siteGuid
    const [expandedSites, setExpandedSites] = useState(new Set());
    // Preloaded site grades for each site (siteGuid -> grade number) - from GetSiteGrade API, so GQCS shows before expand
    const [siteGrades, setSiteGrades] = useState(() => global.gw.customerSitesGradesCache ?? {});
    // Preloaded site issues (siteGuid -> issue string) - from GetAllSitesIssues API, latest unresolved issue per site
    const [siteIssues, setSiteIssues] = useState(() => global.gw.customerSitesIssuesCache ?? {});
    // Flag to prevent saving scroll during restoration
    const isRestoringScrollRef = useRef(false);
    // Flag to prevent multiple restorations
    const hasRestoredRef = useRef(false);
    document.title = "Sites";

    useEffect(() => {
        const onResize = () => setForceUpdate(f => f + 1);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Re-render when weather station readings (including CO2) are loaded so CO2 site cards show data
    useEffect(() => {
        const onWeatherUpdated = () => setForceUpdate(f => f + 1);
        window.addEventListener('grainwave-weather-readings-updated', onWeatherUpdated);
        return () => window.removeEventListener('grainwave-weather-readings-updated', onWeatherUpdated);
    }, []);

    useEffect(() => {
        global.gw.customerSitesGradesCache = siteGrades;
    }, [siteGrades]);

    useEffect(() => {
        global.gw.customerSitesIssuesCache = siteIssues;
    }, [siteIssues]);

    const resumeCustomerSitesDashboard = () => {
        setIsSitesLoaded(true);
        setLoading(false);
        if (global.gw.customerSitesGradesCache) {
            setSiteGrades(global.gw.customerSitesGradesCache);
        }
        if (global.gw.customerSitesIssuesCache) {
            setSiteIssues(global.gw.customerSitesIssuesCache);
        }
        if (global.gw.auth) {
            global.setAllSitesWeatherStationReadings();
        }
        if (!global.gw.dashboardInterval) {
            global.gw.dashboardInterval = setInterval(() => {
                if (global.gw.auth) {
                    global.setAllSitesWeatherStationReadings();
                }
            }, 300000);
        }
    };

    const shouldResumeCustomerSitesFromNavigation = () =>
        sessionStorage.getItem('grainwave_customersites_navigated_away') === 'true'
        && Array.isArray(global.gw.siteInfos)
        && global.gw.siteInfos.length > 0;

    // Restore scroll position and expanded state on mount
    useEffect(() => {
        // Check if we navigated away (set by button/bin clicks)
        // This flag persists across navigation, so it's reliable for detecting return from navigation
        const navigatedAway = sessionStorage.getItem('grainwave_customersites_navigated_away') === 'true';
        
        // Check if page was refreshed (F5, Ctrl+R, browser refresh button)
        // Only check this if we don't have the navigation flag (meaning it's a fresh load or refresh)
        let isPageRefresh = false;
        if (!navigatedAway) {
            // Only check for refresh if we didn't navigate away
            // This prevents false positives during normal navigation
            const navigationEntry = performance.getEntriesByType('navigation')[0];
            isPageRefresh = navigationEntry?.type === 'reload';
        }
        
        // If page was refreshed, clear saved state
        if (isPageRefresh) {
            sessionStorage.removeItem('grainwave_customersites_expanded');
            sessionStorage.removeItem('grainwave_customersites_scroll');
            sessionStorage.removeItem('grainwave_customersites_navigated_away');
            localStorage.removeItem('grainwave_customersites_expanded');
            localStorage.removeItem('grainwave_customersites_scroll');
            return; // Don't restore state on refresh
        }
        
        // Only restore state if we navigated away (came back from button/bin click)
        if (!navigatedAway) {
            return; // Don't restore state on first visit or direct navigation
        }
        
        // Restore expanded sites from sessionStorage (from navigation)
        const sessionExpanded = sessionStorage.getItem('grainwave_customersites_expanded');
        
        if (sessionExpanded) {
            try {
                const expandedArray = JSON.parse(sessionExpanded);
                // One expanded card at a time — restore the most recently saved site if any.
                const only = Array.isArray(expandedArray) && expandedArray.length
                    ? [expandedArray[expandedArray.length - 1]]
                    : [];
                setExpandedSites(new Set(only));
            } catch (e) {
                console.warn('Failed to parse expanded sites:', e);
            }
        }
        
        // Don't clear the flag here - let scroll restoration clear it after both are restored
    }, []);

    // Restore scroll position after sites are loaded and expanded cards have rendered
    // Uses the same approach as SiteFilter: find the element and use scrollIntoView
    useEffect(() => {
        if (!isSitesLoaded) {
            return;
        }

        // Check if we navigated away (set by button/bin clicks)
        const navigatedAway = sessionStorage.getItem('grainwave_customersites_navigated_away') === 'true';
        const savedScroll = sessionStorage.getItem('grainwave_customersites_scroll');
        
        // Only restore scroll if we navigated away (came back from button/bin click)
        // The refresh check is already handled in the expanded state restoration useEffect
        if (!navigatedAway || !savedScroll) {
            return; // Don't restore scroll on first visit, refresh, or if no saved scroll
        }
        
        // Clear the navigation flag now that we're restoring scroll
        // This prevents it from being used again if the component remounts
        sessionStorage.removeItem('grainwave_customersites_navigated_away');

        const scrollPosition = parseInt(savedScroll, 10);
        if (isNaN(scrollPosition) || scrollPosition < 0) {
            return;
        }

        // Reset the restoration flag when we have a new scroll position to restore
        hasRestoredRef.current = false;

        // Use the exact same approach as SiteFilter's adjustScroll function
        const restoreScroll = () => {
            // If we've already restored once, don't do it again
            if (hasRestoredRef.current) {
                return;
            }
            
            // Check all possible scroll positions and determine which element is actually scrollable
            const outletContainer = document.getElementById('outletContainer');
            const windowScroll = window.pageYOffset || 0;
            const docScroll = document.documentElement.scrollTop || 0;
            const bodyScroll = document.body.scrollTop || 0;
            const containerScroll = outletContainer ? outletContainer.scrollTop : 0;
            
            // Determine which element is actually scrollable
            const bodyIsScrollable = document.body.scrollHeight > document.body.clientHeight;
            const docIsScrollable = document.documentElement.scrollHeight > document.documentElement.clientHeight;
            const containerIsScrollable = outletContainer && outletContainer.scrollHeight > outletContainer.clientHeight;
            
            // Get current scroll from the element that is actually scrollable
            let currentScroll = 0;
            if (containerIsScrollable && containerScroll > 0) {
                currentScroll = containerScroll;
            } else if (bodyIsScrollable && bodyScroll > 0) {
                currentScroll = bodyScroll;
            } else if (docIsScrollable && docScroll > 0) {
                currentScroll = docScroll;
            } else if (windowScroll > 0) {
                currentScroll = windowScroll;
            } else {
                currentScroll = Math.max(windowScroll, docScroll, bodyScroll, containerScroll);
            }
            
            const difference = Math.abs(currentScroll - scrollPosition);
            const adjustedDifference = Math.abs(currentScroll - (scrollPosition + 0));
            
            // Check if we're already at the correct position
            // Use a larger tolerance (50px) to prevent re-restoring if we're close
            if (adjustedDifference <= 50 || difference <= 50) {
                hasRestoredRef.current = true; // Mark as restored even if we skip
                return;
            }
            
            // Set flag to prevent scroll saving during restoration
            isRestoringScrollRef.current = true;
            
            // Find all siteCards (same approach as SiteFilter)
            const siteCards = document.getElementsByClassName("siteCard");
            
            if (siteCards.length === 0) {
                // If no cards yet, try again later
                setTimeout(restoreScroll, 100);
                return;
            }
            
            // Skip scrollIntoView - it resets scroll position
            // Just use scrollTo directly with the SiteFilter pattern
            // TEST: Add offset to see where we need to adjust
            // Calculate max scroll for all possible scrollable elements
            const windowMaxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
            const bodyMaxScroll = Math.max(0, document.body.scrollHeight - document.body.clientHeight);
            const containerMaxScroll = outletContainer 
                ? Math.max(0, outletContainer.scrollHeight - outletContainer.clientHeight)
                : 0;
            const maxScroll = Math.max(windowMaxScroll, bodyMaxScroll, containerMaxScroll);
            const adjustedScrollPosition = Math.min(scrollPosition + 0, maxScroll); // Cap at max scroll
            
            // Determine which element is actually scrollable
            let scrollableElement = null;
            let scrollableType = 'window';
            if (containerIsScrollable) {
                scrollableElement = outletContainer;
                scrollableType = 'container';
            } else if (bodyIsScrollable) {
                scrollableElement = document.body;
                scrollableType = 'body';
            } else if (docIsScrollable) {
                scrollableElement = document.documentElement;
                scrollableType = 'document';
            }
            
            // Apply the SiteFilter pattern: do two scrollTo calls
            // First a small adjustment to trigger recalculation (like SiteFilter's -10)
            if (scrollableElement) {
                if (scrollableType === 'container') {
                    scrollableElement.scrollTop = currentScroll - 10;
                } else if (scrollableType === 'body') {
                    scrollableElement.scrollTop = currentScroll - 10;
                } else if (scrollableType === 'document') {
                    scrollableElement.scrollTop = currentScroll - 10;
                }
            } else {
                window.scrollTo(0, currentScroll - 10);
            }
            
            // Clear the flag BEFORE scrolling to target so user can scroll immediately
            isRestoringScrollRef.current = false;
            
            // Then scroll to our adjusted target position
            setTimeout(() => {
                if (scrollableElement) {
                    if (scrollableType === 'container') {
                        scrollableElement.scrollTop = adjustedScrollPosition;
                    } else if (scrollableType === 'body') {
                        scrollableElement.scrollTop = adjustedScrollPosition;
                    } else if (scrollableType === 'document') {
                        scrollableElement.scrollTop = adjustedScrollPosition;
                    }
                } else {
                    window.scrollTo(0, adjustedScrollPosition);
                }
                
                
                // Verify and correct one more time
                setTimeout(() => {
                    // Re-detect scrollable element (in case it changed)
                    const checkContainer = document.getElementById('outletContainer');
                    const checkBodyIsScrollable = document.body.scrollHeight > document.body.clientHeight;
                    const checkDocIsScrollable = document.documentElement.scrollHeight > document.documentElement.clientHeight;
                    const checkContainerIsScrollable = checkContainer && checkContainer.scrollHeight > checkContainer.clientHeight;
                    
                    const windowScroll = window.pageYOffset || 0;
                    const docScroll = document.documentElement.scrollTop || 0;
                    const bodyScroll = document.body.scrollTop || 0;
                    const containerScroll = checkContainer ? checkContainer.scrollTop : 0;
                    
                    let finalScroll = 0;
                    let checkScrollableElement = null;
                    let checkScrollableType = 'window';
                    if (checkContainerIsScrollable && containerScroll > 0) {
                        finalScroll = containerScroll;
                        checkScrollableElement = checkContainer;
                        checkScrollableType = 'container';
                    } else if (checkBodyIsScrollable && bodyScroll > 0) {
                        finalScroll = bodyScroll;
                        checkScrollableElement = document.body;
                        checkScrollableType = 'body';
                    } else if (checkDocIsScrollable && docScroll > 0) {
                        finalScroll = docScroll;
                        checkScrollableElement = document.documentElement;
                        checkScrollableType = 'document';
                    } else if (windowScroll > 0) {
                        finalScroll = windowScroll;
                        checkScrollableType = 'window';
                    } else {
                        finalScroll = Math.max(windowScroll, docScroll, bodyScroll, containerScroll);
                    }
                    
                    if (Math.abs(finalScroll - adjustedScrollPosition) > 5) {
                        if (checkScrollableElement) {
                            if (checkScrollableType === 'container' || checkScrollableType === 'body' || checkScrollableType === 'document') {
                                checkScrollableElement.scrollTop = adjustedScrollPosition;
                            }
                        } else {
                            window.scrollTo(0, adjustedScrollPosition);
                        }
                        
                        setTimeout(() => {
                            // Re-check scrollable element again
                            const checkContainer2 = document.getElementById('outletContainer');
                            const checkBodyIsScrollable2 = document.body.scrollHeight > document.body.clientHeight;
                            const checkDocIsScrollable2 = document.documentElement.scrollHeight > document.documentElement.clientHeight;
                            const checkContainerIsScrollable2 = checkContainer2 && checkContainer2.scrollHeight > checkContainer2.clientHeight;
                            
                            const windowScroll2 = window.pageYOffset || 0;
                            const docScroll2 = document.documentElement.scrollTop || 0;
                            const bodyScroll2 = document.body.scrollTop || 0;
                            const containerScroll2 = checkContainer2 ? checkContainer2.scrollTop : 0;
                            
                            let correctedScroll = 0;
                            let checkScrollableElement2 = null;
                            let checkScrollableType2 = 'window';
                            if (checkContainerIsScrollable2 && containerScroll2 > 0) {
                                correctedScroll = containerScroll2;
                                checkScrollableElement2 = checkContainer2;
                                checkScrollableType2 = 'container';
                            } else if (checkBodyIsScrollable2 && bodyScroll2 > 0) {
                                correctedScroll = bodyScroll2;
                                checkScrollableElement2 = document.body;
                                checkScrollableType2 = 'body';
                            } else if (checkDocIsScrollable2 && docScroll2 > 0) {
                                correctedScroll = docScroll2;
                                checkScrollableElement2 = document.documentElement;
                                checkScrollableType2 = 'document';
                            } else if (windowScroll2 > 0) {
                                correctedScroll = windowScroll2;
                                checkScrollableType2 = 'window';
                            } else {
                                correctedScroll = Math.max(windowScroll2, docScroll2, bodyScroll2, containerScroll2);
                            }
                            
                            // One more final check
                            if (Math.abs(correctedScroll - adjustedScrollPosition) > 5) {
                                if (checkScrollableElement2) {
                                    if (checkScrollableType2 === 'container' || checkScrollableType2 === 'body' || checkScrollableType2 === 'document') {
                                        checkScrollableElement2.scrollTop = adjustedScrollPosition;
                                    }
                                } else {
                                    window.scrollTo(0, adjustedScrollPosition);
                                }
                                
                                setTimeout(() => {
                                    const checkContainer3 = document.getElementById('outletContainer');
                                    const checkBodyIsScrollable3 = document.body.scrollHeight > document.body.clientHeight;
                                    const checkDocIsScrollable3 = document.documentElement.scrollHeight > document.documentElement.clientHeight;
                                    const checkContainerIsScrollable3 = checkContainer3 && checkContainer3.scrollHeight > checkContainer3.clientHeight;
                                    
                                    const windowScroll3 = window.pageYOffset || 0;
                                    const docScroll3 = document.documentElement.scrollTop || 0;
                                    const bodyScroll3 = document.body.scrollTop || 0;
                                    const containerScroll3 = checkContainer3 ? checkContainer3.scrollTop : 0;
                                    
                                    let finalCheck = 0;
                                    if (checkContainerIsScrollable3 && containerScroll3 > 0) {
                                        finalCheck = containerScroll3;
                                    } else if (checkBodyIsScrollable3 && bodyScroll3 > 0) {
                                        finalCheck = bodyScroll3;
                                    } else if (checkDocIsScrollable3 && docScroll3 > 0) {
                                        finalCheck = docScroll3;
                                    } else if (windowScroll3 > 0) {
                                        finalCheck = windowScroll3;
                                    } else {
                                        finalCheck = Math.max(windowScroll3, docScroll3, bodyScroll3, containerScroll3);
                                    }
                                }, 50);
                            }
                        }, 50);
                    }
                    // Log completion
                    setTimeout(() => {
                        // Get final position from the correct scrollable element
                        const finalContainer = document.getElementById('outletContainer');
                        const finalBodyIsScrollable = document.body.scrollHeight > document.body.clientHeight;
                        const finalDocIsScrollable = document.documentElement.scrollHeight > document.documentElement.clientHeight;
                        const finalContainerIsScrollable = finalContainer && finalContainer.scrollHeight > finalContainer.clientHeight;
                        
                        const finalWindowScroll = window.pageYOffset || 0;
                        const finalDocScroll = document.documentElement.scrollTop || 0;
                        const finalBodyScroll = document.body.scrollTop || 0;
                        const finalContainerScroll = finalContainer ? finalContainer.scrollTop : 0;
                        
                        let finalPosition = 0;
                        if (finalContainerIsScrollable && finalContainerScroll > 0) {
                            finalPosition = finalContainerScroll;
                        } else if (finalBodyIsScrollable && finalBodyScroll > 0) {
                            finalPosition = finalBodyScroll;
                        } else if (finalDocIsScrollable && finalDocScroll > 0) {
                            finalPosition = finalDocScroll;
                        } else if (finalWindowScroll > 0) {
                            finalPosition = finalWindowScroll;
                        } else {
                            finalPosition = Math.max(finalWindowScroll, finalDocScroll, finalBodyScroll, finalContainerScroll);
                        }
                        
                        // DISABLED: Final adjustments were preventing user from scrolling
                        // If layout changes after restoration, user can manually scroll
                        // Final adjustment after layout has fully settled (for expanded cards, etc.)
                        // This accounts for any layout changes that happen after initial restoration
                        // setTimeout(() => {
                        //     const settledPosition = window.pageYOffset || document.documentElement.scrollTop || 0;
                        //     const difference = Math.abs(settledPosition - adjustedScrollPosition);
                        //     console.log('[ScrollRestore] Checking final position - current:', settledPosition, 'adjusted target:', adjustedScrollPosition, 'original target:', scrollPosition, 'difference:', difference);
                        //     
                        //     if (difference > 5) {
                        //         console.log('[ScrollRestore] Final adjustment needed');
                        //         window.scrollTo(0, adjustedScrollPosition);
                        //         
                        //         setTimeout(() => {
                        //             const adjustedPosition = window.pageYOffset || document.documentElement.scrollTop || 0;
                        //             console.log('[ScrollRestore] After final adjustment:', adjustedPosition, 'adjusted target:', adjustedScrollPosition, 'original target:', scrollPosition, 'difference:', Math.abs(adjustedPosition - adjustedScrollPosition));
                        //             
                        //             // One more check - if still off, try one more time
                        //             if (Math.abs(adjustedPosition - adjustedScrollPosition) > 5) {
                        //                 console.log('[ScrollRestore] One more adjustment attempt');
                        //                 window.scrollTo(0, adjustedScrollPosition);
                        //             }
                        //         }, 100);
                        //     } else {
                        //         console.log('[ScrollRestore] Position is correct, no adjustment needed');
                        //     }
                        // }, 500); // Wait 500ms for layout to fully settle (increased from 300ms)
                        // 
                        // // One more final check after even longer delay for any very late layout changes
                        // setTimeout(() => {
                        //     const veryFinalPosition = window.pageYOffset || document.documentElement.scrollTop || 0;
                        //     if (Math.abs(veryFinalPosition - adjustedScrollPosition) > 5) {
                        //         console.log('[ScrollRestore] Very final adjustment - current:', veryFinalPosition, 'adjusted target:', adjustedScrollPosition, 'original target:', scrollPosition);
                        //         window.scrollTo(0, adjustedScrollPosition);
                        //     }
                        // }, 1000); // Wait 1 second for any very late layout changes
                    }, 200);
                }, 50);
            }, 10);
        };

        // Wait for DOM to be ready, then restore
        // Use longer delay if there are expanded sites (they need time to render)
        const delay = expandedSites.size > 0 ? 500 : 300;
        
        // First attempt after initial delay
        setTimeout(() => {
            restoreScroll();
        }, delay);
        
        // Also try after a longer delay to catch any late-rendering content
        setTimeout(() => {
            restoreScroll();
        }, delay + 300);
        
        // Mark as restored when we're done (after both attempts)
        setTimeout(() => {
            hasRestoredRef.current = true;
        }, delay + 600);
    }, [isSitesLoaded, expandedSites]);

    // Save scroll position on scroll and before unload
    useEffect(() => {
        let scrollTimeout = null;
        let lastScrollPosition = 0;
        
        const getCurrentScroll = () => {
            // Check all possible scroll positions
            const windowScroll = window.pageYOffset || 0;
            const docScroll = document.documentElement.scrollTop || 0;
            const bodyScroll = document.body.scrollTop || 0;
            const outletContainer = document.getElementById('outletContainer');
            const containerScroll = outletContainer ? outletContainer.scrollTop : 0;
            
            // Determine which element is actually scrollable
            // Check if body is scrollable (has scrollHeight > clientHeight)
            const bodyIsScrollable = document.body.scrollHeight > document.body.clientHeight;
            const docIsScrollable = document.documentElement.scrollHeight > document.documentElement.clientHeight;
            const containerIsScrollable = outletContainer && outletContainer.scrollHeight > outletContainer.clientHeight;
            
            // Use the scroll position from the element that is actually scrollable
            let scrollPosition = 0;
            if (containerIsScrollable && containerScroll > 0) {
                scrollPosition = containerScroll;
            } else if (bodyIsScrollable && bodyScroll > 0) {
                scrollPosition = bodyScroll;
            } else if (docIsScrollable && docScroll > 0) {
                scrollPosition = docScroll;
            } else if (windowScroll > 0) {
                scrollPosition = windowScroll;
            } else {
                // Fallback: use the maximum of all values
                scrollPosition = Math.max(windowScroll, docScroll, bodyScroll, containerScroll);
            }
            
            return scrollPosition;
        };
        
        const handleScroll = (eventSource) => {
            const currentScroll = getCurrentScroll();
            lastScrollPosition = currentScroll;
            
            // Don't save if we're currently restoring scroll position
            if (isRestoringScrollRef.current) {
                return;
            }
            
            // Debounce scroll saving to avoid too frequent writes
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            
            scrollTimeout = setTimeout(() => {
                // Double-check flag before saving
                if (!isRestoringScrollRef.current) {
                    const scrollPosition = getCurrentScroll();
                    sessionStorage.setItem('grainwave_customersites_scroll', scrollPosition.toString());
                    localStorage.setItem('grainwave_customersites_scroll', scrollPosition.toString());
                }
            }, 100); // Save 100ms after scrolling stops
        };

        const handleBeforeUnload = () => {
            // Save scroll position one more time before leaving (synchronous)
            const scrollPosition = getCurrentScroll();
            sessionStorage.setItem('grainwave_customersites_scroll', scrollPosition.toString());
            localStorage.setItem('grainwave_customersites_scroll', scrollPosition.toString());
        };

        // Also save on visibility change (when tab becomes hidden)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                const scrollPosition = getCurrentScroll();
                sessionStorage.setItem('grainwave_customersites_scroll', scrollPosition.toString());
                localStorage.setItem('grainwave_customersites_scroll', scrollPosition.toString());
            }
        };

        // Store handlers so we can remove them later
        const handlers = {
            window: null,
            document: null,
            body: null,
            outletContainer: null
        };
        
        // Add listeners to ALL possible scrollable elements
        const setupScrollListeners = () => {
            // Listen to window scroll
            handlers.window = () => handleScroll('window');
            window.addEventListener('scroll', handlers.window, { passive: true });
            
            // Listen to document scroll
            handlers.document = () => handleScroll('document');
            document.addEventListener('scroll', handlers.document, { passive: true });
            
            // Listen to body scroll
            handlers.body = () => handleScroll('body');
            document.body.addEventListener('scroll', handlers.body, { passive: true });
            
            // Listen to outletContainer scroll
            const outletContainer = document.getElementById('outletContainer');
            if (outletContainer) {
                handlers.outletContainer = () => handleScroll('outletContainer');
                outletContainer.addEventListener('scroll', handlers.outletContainer, { passive: true });
            } else {
                // Retry after a short delay
                setTimeout(setupScrollListeners, 100);
                return;
            }
            
            window.addEventListener('beforeunload', handleBeforeUnload);
            document.addEventListener('visibilitychange', handleVisibilityChange);
        };
        
        // Start setting up listeners
        setupScrollListeners();
        
        return () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            // Remove all scroll listeners
            if (handlers.window) {
                window.removeEventListener('scroll', handlers.window);
            }
            if (handlers.document) {
                document.removeEventListener('scroll', handlers.document);
            }
            if (handlers.body) {
                document.body.removeEventListener('scroll', handlers.body);
            }
            if (handlers.outletContainer) {
                const outletContainer = document.getElementById('outletContainer');
                if (outletContainer) {
                    outletContainer.removeEventListener('scroll', handlers.outletContainer);
                }
            }
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Save expanded state when it changes
    useEffect(() => {
        // Always save the state, even if empty, so we can restore the collapsed state
        const expandedArray = Array.from(expandedSites);
        sessionStorage.setItem('grainwave_customersites_expanded', JSON.stringify(expandedArray));
        localStorage.setItem('grainwave_customersites_expanded', JSON.stringify(expandedArray));
    }, [expandedSites]);

    useEffect(() => {
        // Wait for token to be available before initializing
        // This ensures the token is restored from localStorage before making API calls
        const initializeWhenReady = () => {
            if (global.gw.token) {
                if (shouldResumeCustomerSitesFromNavigation()) {
                    resumeCustomerSitesDashboard();
                } else {
                    initializeCustomerSites();
                }
                return true;
            }
            return false;
        };

        // Try immediately
        if (initializeWhenReady()) {
            return;
        }

        // If token isn't available yet, wait a bit and try again
        // This handles the case where token restoration is still in progress
        let checkCount = 0;
        const maxChecks = 50; // 5 seconds total (50 * 100ms)
        const checkToken = setInterval(() => {
            checkCount++;
            if (initializeWhenReady()) {
                clearInterval(checkToken);
            } else if (checkCount >= maxChecks) {
                console.warn('CustomerSites: Token not available after waiting, attempting initialization anyway...');
                clearInterval(checkToken);
                if (shouldResumeCustomerSitesFromNavigation()) {
                    resumeCustomerSitesDashboard();
                } else {
                    initializeCustomerSites();
                }
            }
        }, 100);
        
        return () => clearInterval(checkToken);
    }, []);

    // Cleanup: Cancel weather station API calls and clear interval when component unmounts
    useEffect(() => {
        return () => {
            // Cancel any ongoing weather station API calls
            if (global.gw.weatherStationAbortController) {
                global.gw.weatherStationAbortController.abort();
                global.gw.weatherStationAbortController = null;
            }
            // Clear the dashboard interval
            if (global.gw.dashboardInterval) {
                clearInterval(global.gw.dashboardInterval);
                global.gw.dashboardInterval = null;
            }
        };
    }, []);

    // Listen for theme changes and force re-render
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(Date.now());
        };

        window.addEventListener('themeChanged', handleThemeChange);

        const handleSiteIssuesRefreshed = async () => {
            if (global.gw.siteInfos?.length > 0) {
                try {
                    const issuesList = await createApiEndpoint().fetchAllSitesIssues("direct");
                    if (Array.isArray(issuesList)) {
                        const issuesMap = {};
                        issuesList.forEach((item) => {
                            if (item?.siteGuid && item?.issue) {
                                issuesMap[item.siteGuid] = item.issue;
                            }
                        });
                        setSiteIssues(issuesMap);
                    }
                } catch (err) {
                    console.error('Error refreshing site issues:', err);
                }
            }
        };
        window.addEventListener('siteIssuesRefreshed', handleSiteIssuesRefreshed);

        return () => {
            window.removeEventListener('themeChanged', handleThemeChange);
            window.removeEventListener('siteIssuesRefreshed', handleSiteIssuesRefreshed);
        };
    }, []);

    // Also listen for the global theme change event
    useEffect(() => {
        if (global.gw.themeChanged) {
            setForceUpdate(Date.now());
        }
    }, [global.gw.themeChanged]);

    const initializeCustomerSites = () => {
        (async () => {
            try {
                // Check network connectivity
                if (!navigator.onLine) {
                    if (global.gw.siteInfos && global.gw.siteInfos.length > 0) {
                        setIsSitesLoaded(true);
                        setLoading(false);
                        // Cannot fetch bin grades when offline - use any previously loaded grades from siteBinGrades
                        return;
                    }
                }
                
                global.gw.currentWeatherReadings = [];

                // if (global.gw.currentWeatherReadings.length === 0) {
                //     // console.log('CustomerSites: Fetching weather station readings...');
                //     try {
                //         // Add timeout to prevent hanging
                //         const weatherPromise = global.setAllSitesWeatherStationReadings();
                //         const timeoutPromise = new Promise((_, reject) => 
                //             setTimeout(() => reject(new Error('Weather API timeout')), 3000)
                //         );
                        
                //         await Promise.race([weatherPromise, timeoutPromise]);
                //         // console.log('CustomerSites: Weather station readings completed');
                //     } catch (weatherError) {
                //         // console.warn('CustomerSites: Weather API failed, continuing without weather data:', weatherError.message);
                //         // Continue without weather data rather than failing completely
                //         global.gw.currentWeatherReadings = [];
                //     }
                // } else {
                //     // console.log('CustomerSites: Using cached weather station readings');
                // }

                // console.log('CustomerSites: Fetching user sites...');
                // console.log('CustomerSites: Current token:', global.gw.token ? 'present' : 'missing');
                // console.log('CustomerSites: Current auth:', global.gw.auth ? 'present' : 'missing');
                // console.log('CustomerSites: Current email:', global.gw.email);
                
                // Try to fetch user sites with retry mechanism
                let ccrSiteIds = [];
                try {
                    ccrSiteIds = await createApiEndpoint().fetchCcrSiteIds("direct");
                } catch {
                    ccrSiteIds = [];
                }
                const ccrSiteIdSet = new Set(
                    (Array.isArray(ccrSiteIds) ? ccrSiteIds : []).map((id) => global.normalizeGuidForCompare(id))
                );

                let response = null;
                let retryCount = 0;
                const maxRetries = 2;
                
                while (!response && retryCount <= maxRetries) {
                    try {
                        response = await createApiEndpoint().fetchUserSites("direct");
                        
                        // Check if response is false (which might indicate a 401 that was caught)
                        if (response === false) {
                            // false = network/decrypt/parse failure — not necessarily auth. Retry / fallback below.
                            response = null;
                        } else if (response && response.data) {
                            break;
                        } else {
                            //console.warn(`CustomerSites: Attempt ${retryCount + 1} returned invalid response:`, response);
                            response = null;
                        }
                    } catch (error) {
                        //console.error(`CustomerSites: Attempt ${retryCount + 1} failed:`, error.message);
                        //console.error('CustomerSites: Error details:', error);
                        
                        // If it's a 401 error, re-throw it so the axios interceptor can handle it
                        if (error.response && error.response.status === 401) {
                            throw error; // Re-throw 401 errors so the global interceptor can handle them
                        }
                        // Also check for error message ending with "401"
                        if (error.message && error.message.endsWith("401")) {
                            // Create a proper error object with 401 status
                            const authError = new Error(error.message);
                            authError.response = { status: 401 };
                            throw authError; // Re-throw as 401 so the interceptor can handle it
                        }
                        
                        response = null;
                    }
                    
                    retryCount++;
                    if (retryCount <= maxRetries) {
                        //console.log(`CustomerSites: Waiting 2 seconds before retry ${retryCount + 1}...`);
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                }
                
                if (!response) {
                    console.error('CustomerSites: fetchUserSites failed after all retries');
                    
                    // If we have a token but got no response, it might be an auth issue
                    // Try to validate the token by making a simple API call
                    if (global.gw.token) {
                        console.warn('CustomerSites: No response but token present, checking if token is valid...');
                        try {
                            const profileResponse = await createApiEndpoint().fetchProfile("direct");
                            // Only treat real HTTP 401 as auth failure (axios throws). false/empty = transient or offline.
                            if (profileResponse && profileResponse.data && profileResponse.data.fetchProfile) {
                                // Token path works; sites fetch likely transient — stay logged in and show empty/cached below.
                            }
                        } catch (profileError) {
                            if (profileError.response && profileError.response.status === 401) {
                                throw profileError;
                            }
                        }
                    }
                    
                    // Check if we have cached site data to fall back to
                    if (global.gw.siteInfos && global.gw.siteInfos.length > 0) {
                        //console.log('CustomerSites: Using cached site data as fallback');
                        setIsSitesLoaded(true);
                        setLoading(false);
                        // Fetch site issues for all cached sites
                        try {
                            const issuesList = await createApiEndpoint().fetchAllSitesIssues("direct");
                            if (Array.isArray(issuesList)) {
                                const issuesMap = {};
                                issuesList.forEach((item) => {
                                    if (item?.siteGuid && item?.issue) {
                                        issuesMap[item.siteGuid] = item.issue;
                                    }
                                });
                                setSiteIssues(issuesMap);
                            }
                        } catch (err) {
                            console.error('Error fetching site issues:', err);
                        }
                        // Fetch site grades for cached sites so GQCS displays on collapsed cards
                        // Use Promise.all + single setState to avoid race condition where grades bleed between sites
                        const nonHmsSites = global.gw.siteInfos.filter(s => !s.isHms && !s.isCcr && s.siteGuid);
                        const gradesMap = {};
                        await Promise.all(nonHmsSites.map(async (site) => {
                            try {
                                const siteGradeResponse = await createApiEndpoint().fetchSiteGrade("direct", { date: null }, site.siteGuid);
                                if (siteGradeResponse?.data?.getSiteGrade) {
                                    const arrayBuffer = global.base64StringToArrayBuffer(siteGradeResponse.data.getSiteGrade);
                                    const decompressed = await global.decompress(arrayBuffer, "gzip");
                                    const responseData = JSON.parse(decompressed);
                                    const grade = responseData?.Grade;
                                    const hasValidGrade = grade != null && !isNaN(Number(grade));
                                    gradesMap[site.siteGuid] = hasValidGrade ? Number(grade) : undefined;
                                } else {
                                    gradesMap[site.siteGuid] = undefined;
                                }
                            } catch (err) {
                                console.error('Error fetching site grade for:', site.siteName, err);
                                gradesMap[site.siteGuid] = undefined;
                            }
                        }));
                        setSiteGrades(gradesMap);
                        // Load weather readings so CO2 sites show data when using cached fallback
                        if (global.gw.auth) {
                            global.setAllSitesWeatherStationReadings();
                        }
                        return;
                    }
                    
                    // If no cached data, show error state
                    console.error('CustomerSites: No cached data available, showing empty state');
                    setLoading(false);
                    setIsSitesLoaded(true);
                    return;
                }
                
                if (!response.data) {
                    console.error('CustomerSites: Response has no data property:', response);
                    setLoading(false);
                    setIsSitesLoaded(true);
                    return;
                }
                
                // console.log('CustomerSites: Response structure:', {
                //     hasData: !!response.data,
                //     hasFetchUserAttribute: !!(response.data && response.data.fetchUserAttribute),
                //     hasSites: !!(response.data && response.data.fetchUserAttribute && response.data.fetchUserAttribute.sites),
                //     sitesLength: response.data && response.data.fetchUserAttribute && response.data.fetchUserAttribute.sites ? response.data.fetchUserAttribute.sites.length : 0
                // });
                
            if (response &&
                response.data &&
                response.data.fetchUserAttribute &&
                response.data.fetchUserAttribute.sites) {
                
                // Handle case where sites array exists but might be empty
                if (response.data.fetchUserAttribute.sites.length === 0) {
                    // console.log('CustomerSites: No sites found in response');
                    global.gw.siteInfos = [];
                    setIsSitesLoaded(true);
                    setLoading(false);
                    return;
                }
                
                //document.documentElement.scrollTop = document.body.scrollTop = 100;

                global.gw.siteInfos = [];
                setSiteGrades({}); // Clear stale grades from previous load
                setSiteIssues({}); // Clear stale issues from previous load

                try {
                    response.data.fetchUserAttribute.sites.map((_, siteIndex) => {
                        // console.log(`CustomerSites: Processing site ${siteIndex + 1}/${response.data.fetchUserAttribute.sites.length}: ${_.main?.siteName || 'Unknown'}`);
                        const siteStartTime = performance.now();
                        
                        // CO2 sites may have null/undefined lastScan from scan API - use fallback
                        const lastScanRaw = _.lastScan;
                        const lastScanDate = lastScanRaw
                            ? new Date(typeof lastScanRaw === 'string' ? lastScanRaw.split(".")[0] : lastScanRaw)
                            : null;
                        const lastScanFormatted = lastScanRaw && lastScanDate && !isNaN(lastScanDate.getTime())
                            ? lastScanDate.toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "")
                            : "N/A";
                        let siteInfo = {
                            siteIndex: siteIndex,
                            siteGuid: _.siteGuid,
                            siteName: _.main.siteName,
                            emailReportTo: _.main?.emailReportTo ?? _.main?.EmailReportTo ?? '',
                            bins: _.bins ?? [],
                            cables: _.cables ?? [],
                            isHms: _.isHms,
                            isCcr: _.isCcr === true || ccrSiteIdSet.has(global.normalizeGuidForCompare(_.siteGuid)),
                            currentTemperature: _.currentTemperature,
                            currentHumidity: _.currentHumidity,
                            currentPressure: _.currentPressue,
                            lastScanDate: lastScanDate,
                            lastScan: lastScanFormatted,
                            taskSlots: Array.isArray(_.taskSlots) ? _.taskSlots : [],
                            siteWeatherStationReadingSettings: [],
                        };

                        const weatherFilterStartTime = performance.now();
                        siteInfo.siteWeatherStationReadings = (global.gw.currentWeatherReadings || []).filter(x => global.normalizeGuidForCompare(x.siteId ?? x.SiteId) === global.normalizeGuidForCompare(siteInfo.siteGuid));
                        const weatherFilterEndTime = performance.now();
                        
                        if (weatherFilterEndTime - weatherFilterStartTime > 5) {
                            // console.log(`Site ${siteIndex + 1} weather filtering took ${(weatherFilterEndTime - weatherFilterStartTime).toFixed(2)}ms`);
                        }

                        let ss = global.gw.siteInfos.map((x, i) => {
                            if (x.siteGuid === siteInfo.siteGuid) {
                                return { x, i };
                            }
                        })[0];

                        if (global.gw.siteInfos.length === 0 || !ss) {
                            global.gw.siteInfos.push(siteInfo);
                        }
                        else {
                            global.gw.siteInfos[ss.i] = siteInfo;
                        }
                    });
                    global.gw.siteInfos.sort((a, b) =>
                        String(a.siteName || "").localeCompare(String(b.siteName || ""), undefined, {
                            sensitivity: "base",
                        }),
                    );
                    global.gw.siteInfos.forEach((s, i) => {
                        s.siteIndex = i;
                    });
                    setIsSitesLoaded(true);
                    setLoading(false);

                    // Fetch site issues for all sites
                    try {
                        const issuesList = await createApiEndpoint().fetchAllSitesIssues("direct");
                        if (Array.isArray(issuesList)) {
                            const issuesMap = {};
                            issuesList.forEach((item) => {
                                if (item?.siteGuid && item?.issue) {
                                    issuesMap[item.siteGuid] = item.issue;
                                }
                            });
                            setSiteIssues(issuesMap);
                        }
                    } catch (err) {
                        console.error('Error fetching site issues:', err);
                    }
                    // Fetch site grades for all non-HMS sites so GQCS displays on collapsed cards before expand
                    // Use Promise.all + single setState to avoid race condition where grades bleed between sites
                    const nonHmsSites = global.gw.siteInfos.filter(s => !s.isHms && !s.isCcr && s.siteGuid);
                    const gradesMap = {};
                    await Promise.all(nonHmsSites.map(async (site) => {
                        try {
                            const siteGradeResponse = await createApiEndpoint().fetchSiteGrade("direct", { date: null }, site.siteGuid);
                            if (siteGradeResponse?.data?.getSiteGrade) {
                                const arrayBuffer = global.base64StringToArrayBuffer(siteGradeResponse.data.getSiteGrade);
                                const decompressed = await global.decompress(arrayBuffer, "gzip");
                                const responseData = JSON.parse(decompressed);
                                const grade = responseData?.Grade;
                                const hasValidGrade = grade != null && !isNaN(Number(grade));
                                gradesMap[site.siteGuid] = hasValidGrade ? Number(grade) : undefined;
                            } else {
                                gradesMap[site.siteGuid] = undefined;
                            }
                        } catch (err) {
                            console.error('Error fetching site grade for site:', site.siteName, err);
                            gradesMap[site.siteGuid] = undefined;
                        }
                    }));
                    setSiteGrades(gradesMap);
                } catch (error) {
                    if (global.gw.test) {
                        // console.log(error.message);
                    }

                    if (error.message &&
                        error.message.endsWith("401") &&
                        global.gw.token &&
                        global.gw.auth) {
                        createApiEndpoint(global.Endpoints.GrainWave)
                            .fetchRenew()
                            .then(r => {
                                if (r && global.gw.token) {
                                    // Update the auth context with the new token
                                    setToken(global.gw.token);
                                    initializeCustomerSites();
                                } else {
                                    // Token renewal failed, redirect to login
                                    navigate("/", { replace: true });
                                    setIsSitesLoaded(true);
                                    setLoading(false);
                                }
                            })
                            .catch(() => {
                                // Token renewal failed, redirect to login
                                navigate("/", { replace: true });
                                setIsSitesLoaded(true);
                                setLoading(false);
                            });
                    }
                    else {
                        console.error('CustomerSites: Error processing sites, redirecting to login');
                        navigate("/", { replace: true });
                        setIsSitesLoaded(true);
                        setLoading(false);
                    }
                }
            }
            else {
                console.warn('CustomerSites: No sites found in response or invalid response structure');
                console.warn('CustomerSites: Response structure:', {
                    hasData: !!response.data,
                    hasFetchUserAttribute: !!(response.data && response.data.fetchUserAttribute),
                    hasSites: !!(response.data && response.data.fetchUserAttribute && response.data.fetchUserAttribute.sites),
                    sitesLength: response.data && response.data.fetchUserAttribute && response.data.fetchUserAttribute.sites ? response.data.fetchUserAttribute.sites.length : 0
                });
                // Still set loading states even if no sites
                global.gw.siteInfos = [];
                setIsSitesLoaded(true);
                setLoading(false);
            }

            // Load weather station readings (including CO2) immediately so CO2 sites show data on site cards
            if (global.gw.auth) {
                global.setAllSitesWeatherStationReadings();
            }
            if (!global.gw.dashboardInterval) {
                global.gw.dashboardInterval = setInterval(() => {
                    if (global.gw.auth) {
                        global.setAllSitesWeatherStationReadings();
                    }
                }, 300000);
            }
            } catch (error) {
                console.error('Error initializing customer sites:', error);
                
                // If it's a 401 error, manually trigger the redirect (interceptor only catches axios errors)
                if (error.response && error.response.status === 401) {
                    // console.log('CustomerSites: 401 error detected, clearing auth and redirecting to login');
                    // Clear authentication state
                    clearToken();
                    delete axios.defaults.headers.common["Authorization"];
                    global.gw.token = null;
                    global.gw.auth = null;
                    global.gw.isAuthenticated = false;
                    // Redirect to login page
                    setTimeout(() => {
                        window.location.href = "/emailLogin";
                    }, 100);
                    return;
                }
                // Also check for error message containing "401"
                if (error.message && (error.message.includes("401") || error.message.includes("Unauthorized"))) {
                    // console.log('CustomerSites: Unauthorized error detected, clearing auth and redirecting to login');
                    // Clear authentication state
                    clearToken();
                    delete axios.defaults.headers.common["Authorization"];
                    global.gw.token = null;
                    global.gw.auth = null;
                    global.gw.isAuthenticated = false;
                    // Redirect to login page
                    setTimeout(() => {
                        window.location.href = "/emailLogin";
                    }, 100);
                    return;
                }
                
                // Handle timeout errors specifically
                if (error.message === 'Weather API timeout' || error.message === 'Sites API timeout') {
                    console.warn('API timeout occurred, retrying with cached data...');
                    // Try to continue with cached data if available
                    if (global.gw.siteInfos && global.gw.siteInfos.length > 0) {
                        setIsSitesLoaded(true);
                        setLoading(false);
                        return;
                    }
                }
                
                setLoading(false);
                setIsSitesLoaded(true);
            }
        })();
    }

    const dismissAlphaModal = () => {
        global.gw.isAlphaModalOpen = false;
        rememberAlphaWelcomeDismissed();
        setForceUpdate((n) => n + 1);
    };

    const renderAlphaModal = () => {
        return (
            <ConfirmDialog
                open={global.gw.isAlphaModalOpen && !alphaWelcomeDismissed()}
                onClose={dismissAlphaModal}
                title="GrainWave is in Alpha"
                titleId="alpha-modal-title"
                actions={(
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={confirmDialogButtonSx()}
                        onClick={dismissAlphaModal}
                    >
                        OK
                    </MuiMaterials.Button>
                )}
            >
                <MuiMaterials.Typography
                    variant="body2"
                    sx={{
                        mt: 1,
                        maxWidth: 440,
                        color: getThemeColor('#444444', '#c4a574'),
                    }}
                >
                    TempuTech GrainWave is in Alpha. You have been given exclusive access in order to test features and report any improvements. Please use the feedback tool if you have any suggestions, or encounter any bugs that you would like addressed. The feedback tool can be accessed by clicking on the user icon and selecting Feedback. Thanks for using GrainWave!
                </MuiMaterials.Typography>
            </ConfirmDialog>
        );
    }

    if (isLoading) {
        return (
            <MuiMaterials.Box>
                {renderAlphaModal()}
                <MuiMaterials.Box
                    sx={{
                        fontSize: "50px",
                        color: getThemeColor('#2d2d2d', 'rgb(244, 204, 142)'),
                        margin: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "fixed",
                        top: "60px",
                        left: "2px",
                        bottom: "-2px",
                        right: "1px",
                    }}
                >
                    <MuiMaterials.RotatingLines
                        strokeColor={getThemeColor('#2d2d2d', '#f4cc8e')}
                        strokeWidth="3"
                        animationDuration="0.72"
                        width="48"
                        //visible={true}
                    />
                </MuiMaterials.Box>
            </MuiMaterials.Box>
        );
    }

    return (
        false ?
            null
            :
            <MuiMaterials.Box
                sx={{
                    flexGrow: 1,
                    bgcolor: getThemeColor('#e8e8e8', '#1a1a1a'),
                    minWidth: '100%',
                    minHeight: '100%',
                    display: 'block',
                    touchAction: 'pan-y',
                    px: { xs: 1, sm: 1.5, md: 2 },
                    pt: { xs: "12px", sm: "12px", md: "12px" },
                    pb: {
                        xs: "calc(24px + env(safe-area-inset-bottom, 0px))",
                        sm: 2,
                    },
                    boxSizing: 'border-box',
                }}
            >
                {renderAlphaModal()}
                <MuiMaterials.Box
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        mx: 'auto',
                        boxSizing: 'border-box',
                        p: { xs: '8px 10px', sm: '12px 14px' },
                        borderRadius: '12px',
                        border: `1px solid ${getThemeColor('rgba(0, 0, 0, 0.12)', 'rgba(244, 204, 142, 0.28)')}`,
                        background: getThemeColor('#f8f8f8', '#313131'),
                        boxShadow: getThemeColor(
                            '0 2px 8px rgba(0, 0, 0, 0.04)',
                            '0 4px 16px rgba(0, 0, 0, 0.2)',
                        ),
                    }}
                >
                    <MuiMaterials.Box
                        className="site-cards-list"
                        sx={{
                            width: '100%',
                            borderRadius: '8px',
                            border: `1px solid ${getThemeColor('#d8d8d8', '#3d3d3d')}`,
                            background: getThemeColor('#ffffff', '#2a2a2a'),
                            boxShadow: getThemeColor(
                                'inset 0 1px 0 rgba(255,255,255,0.8)',
                                'inset 0 1px 0 rgba(255,255,255,0.04)',
                            ),
                            overflow: 'hidden',
                            touchAction: 'pan-y',
                            // Inventory-style row rhythm: shared surface, hairline dividers, stripe + hover.
                            '& > .siteCard': {
                                margin: 0,
                                borderRadius: 0,
                                border: 'none',
                                borderBottom: `1px solid ${getThemeColor('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.06)')}`,
                                boxShadow: 'none',
                                background: 'transparent',
                                backdropFilter: 'none',
                                WebkitBackdropFilter: 'none',
                            },
                            '& > .siteCard:last-child': {
                                borderBottom: 'none',
                            },
                            '& > .siteCard:hover': {
                                background: getThemeColor('rgba(0, 0, 0, 0.025)', 'rgba(244, 204, 142, 0.05)'),
                            },
                            // Expanded rows stay on the darker editor-style canvas (no hover/stripe override).
                            '& > .siteCard.siteCard--expanded, & > .siteCard.siteCard--expanded:hover': {
                                background: `${getThemeColor('#e8e8e8', '#1e1e1e')} !important`,
                            },
                        }}
                    >
                        {isSitesLoaded && [...(global.gw.siteInfos || [])]
                            .sort((a, b) =>
                                String(a.siteName || "").localeCompare(String(b.siteName || ""), undefined, {
                                    sensitivity: "base",
                                }),
                            )
                            .map((siteInfo, index) => (
                            <SiteCard
                                key={`dashboard_sitecard${siteInfo.siteGuid}`}
                                siteIndex={index}
                                siteGuid={siteInfo.siteGuid}
                                siteGrade={siteGrades[siteInfo.siteGuid]}
                                siteIssue={siteIssues[siteInfo.siteGuid]}
                                isExpanded={expandedSites.has(siteInfo.siteGuid)}
                                onExpandedChange={(expanded) => {
                                    setExpandedSites(() => (
                                        expanded ? new Set([siteInfo.siteGuid]) : new Set()
                                    ));
                                }}
                            />
                        ))}
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
                <div style={{ height: "24px" }} />
            </MuiMaterials.Box>
    );
}

export default CustomerSites;